import React, { useContext, useState, useEffect, Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Checkbox, Typography, Grid, TextField, FormControlLabel, Switch } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";

// Contexts
import CampaignContext from "../../../context/campaign/CampaignContext";
import ParameterContext from "../../../context/parameters/ParameterContext";
import FacebookProfileContext from "../../../context/facebook_profiles/dashboard/DashboardContext";
import UserContext from "../../../context/users/UserContext";

const useStyles = makeStyles(() => ({
  checkbox: {
    margin: 0,
  },
  section: {
    marginTop: "0.1rem",
  },
  skeleton: {
    borderRadius: "5px",
  },
}));

const Section_02 = () => {
  const classes = useStyles();

  const { agency_account, toggleAgencyAccount, form_data, form_error, setFormData, setFormError, setCheckoutData } =
    useContext(CampaignContext);
  const { getCheckoutLanguages, posting_store, domain_list, getPostingStore, getDomainList } =
    useContext(ParameterContext);
  const { profiles, getProfiles } = useContext(FacebookProfileContext);
  const { users, getUsers } = useContext(UserContext);

  const [bm_list, setBmList] = useState([]);
  const [ad_list, setAdList] = useState([]);
  const [pixel_list, setPixelList] = useState([]);

  const handleChange = (e) => {
    const id = e.target.id;
    let value = e.target.value;

    if (id === "laptop") {
      form_data["bm_account"] = "";
      form_data["ad_account"] = "";
      form_data["pixel_id"] = "";
    } else if (id === "bm_account") {
      form_data["ad_account"] = "";
      form_data["pixel_id"] = "";
    } else if (id === "ad_account") {
      form_data["pixel_id"] = "";
    }

    if (["domain", "pixel_access_token"].includes(id)) {
      form_data[id] = value.trim();
    } else if (id === "status") {
      form_data[id] = e.target.checked ? "ACTIVE" : "PAUSE";
      form_data["paused_at"] = e.target.checked ? "" : moment().format();
    } else {
      form_data[id] = value;
    }

    if (id === "posting_store") {
      form_data["product_id"] = "";
      form_data["deals"] = {
        recommended: "",
        super_saver: "",
      };
      form_data["presell"] = {};
      setCheckoutData(null);
    }

    switch (id) {
      case "laptop":
      case "bm_account":
      case "ad_account":
      case "pixel_id":
      case "posting_store":
      case "campaign_owner":
      case "media_buyer":
        let name = "";

        if (id === "laptop") name = "Laptop";
        else if (id === "bm_account") name = "Facebook Business Manager";
        else if (id === "ad_account") name = "Facebook Ad Account";
        else if (id === "pixel_id") name = "Facebook Pixel";
        else if (id === "posting_store") name = "Posting Store";
        else if (id === "campaign_owner") name = "Owner";
        else if (id === "media_buyer") name = "Media Buyer";

        if (value === "") form_error[id] = `Select one ${name}!`;
        else form_error[id] = "";
        break;

      case "domain":
        if (value.length < 1) {
          form_error[id] = "Field cannot be empty!";
        } else {
          form_error[id] = "";
        }
        break;

      case "angle":
        if (value.length < 1) {
          form_error[id] = "Field cannot be empty!";
        } else if (value.length > 50) {
          form_error[id] = "Angle cannot be more than 50 characters!";
        } else {
          form_error[id] = "";
        }
        break;

      case "pixel_access_token":
        if (value.length < 1) {
          form_error[id] = "Field cannot be empty!";
        } else {
          form_error[id] = "";
        }
        break;

      default:
        break;
    }

    setFormData(form_data);
    setFormError(form_error);
  };

  useEffect(() => {
    getCheckoutLanguages();
    getPostingStore();
    getProfiles();
    getUsers();
    getDomainList();
  }, []);

  useEffect(() => {
    // Update Facebook Lists (BM Account, Ad Account, Pixel)
    if (profiles && form_data.laptop) {
      let profile = profiles.filter((each) => each.profile_id === form_data.laptop);

      if (profile.length === 1) {
        profile = profile[0];
        setBmList(profile.bm_account);

        let temp_ad = [];
        let temp_pixel = [];

        // console.log(form_data);
        if (form_data.bm_account) {
          const bm_acc_id = profile.bm_account.find(acc => acc.bm_account_id === form_data.bm_account)?.id;
          temp_ad = profile.ad_account.filter((each) => each.bm_account_id === bm_acc_id);
        }

        if (form_data.ad_account) {
          temp_ad.forEach((each) => {
            if (each.ad_account_id === form_data.ad_account) {
              temp_pixel = each.pixels;
            }
          });
        }

        setAdList(temp_ad);
        setPixelList(temp_pixel);
      } else {
        setBmList([]);
        setAdList([]);
        setPixelList([]);
      }
    }
  }, [profiles, form_data.laptop, form_data.bm_account, form_data.ad_account]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="caption" color="primary">
          Step 2: Campaign Details
        </Typography>
      </Grid>
      {profiles ? (
        <Fragment>
          <Grid
            item
            xs={12}
            spacing={2}
            container
            alignItems="center"
            justifyContent="flex-start"
            className={classes.section}
          >
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox size="small" color="primary" checked={agency_account} onChange={toggleAgencyAccount} />
                }
                label="Agency Account"
                className={classes.checkbox}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography component="div">
                <Grid container alignItems="center">
                  <Grid item>
                    <Switch
                      id="status"
                      size="small"
                      color="primary"
                      checked={form_data.status === "ACTIVE" ? true : false}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item>&ensp; Campaign Status: {form_data.status}</Grid>
                </Grid>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Laptop"
                id="laptop"
                name="laptop"
                onChange={handleChange}
                value={profiles && form_data.laptop}
                error={form_error.laptop ? true : false}
                helperText={form_error.laptop}
              >
                <option value="" disabled />
                {profiles &&
                  profiles.map((each) => {
                    return (
                      <option key={each.profile_id} value={each.profile_id}>
                        {each.profile_id}
                      </option>
                    );
                  })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Domain"
                id="domain"
                name="domain"
                onChange={handleChange}
                value={form_data.domain}
                error={form_error.domain ? true : false}
                helperText={form_error.domain}
              >
                <option value="" disabled />
                {domain_list &&
                  domain_list.map((each) => {
                    return (
                      <option key={each.name} value={each.name}>
                        {each.name}
                      </option>
                    );
                  })}
              </TextField>
            </Grid>
          </Grid>
          <Grid item xs={12} spacing={2} container alignItems="flex-start" justifyContent="flex-start">
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Facebook Business Manager"
                id="bm_account"
                name="bm_account"
                onChange={handleChange}
                value={bm_list.length > 0 && form_data.bm_account}
                error={form_error.bm_account ? true : false}
                helperText={form_error.bm_account}
              >
                <option value="" disabled />
                {bm_list.length > 0 &&
                  bm_list.map((each, index) => {
                    return (
                      <option key={index} value={each.bm_account_id}>
                        {each.bm_account_name}({each.bm_account_id})
                      </option>
                    );
                  })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                label="Angle"
                id="angle"
                name="angle"
                onChange={handleChange}
                value={form_data.angle}
                error={form_error.angle ? true : false}
                helperText={form_error.angle}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} spacing={2} container alignItems="flex-start" justifyContent="flex-start">
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Facebook Ad Account"
                id="ad_account"
                name="ad_account"
                onChange={handleChange}
                value={ad_list.length > 0 && form_data.ad_account}
                error={form_error.ad_account ? true : false}
                helperText={form_error.ad_account}
              >
                <option value="" disabled />
                {ad_list.length > 0 &&
                  ad_list.map((each, index) => {
                    return (
                      <option key={index} value={each.ad_account_id}>
                        {each.ad_account_name}({each.ad_account_id})
                      </option>
                    );
                  })}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Posting Store"
                id="posting_store"
                name="posting_store"
                onChange={handleChange}
                value={posting_store?.length > 0 && form_data.posting_store}
                error={form_error.posting_store ? true : false}
                helperText={form_error.posting_store}
              >
                <option value="" disabled />
                {posting_store?.length > 0 &&
                  posting_store.map((each, index) => {
                    return (
                      <option key={index} value={each}>
                        {each}
                      </option>
                    );
                  })}
              </TextField>
            </Grid>
          </Grid>
          <Grid item xs={12} spacing={2} container alignItems="flex-start" justifyContent="flex-start">
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Facebook Pixel"
                id="pixel_id"
                name="pixel_id"
                onChange={handleChange}
                value={pixel_list.length > 0 && form_data.pixel_id}
                error={form_error.pixel_id ? true : false}
                helperText={form_error.pixel_id}
              >
                <option value="" disabled />
                {pixel_list.length > 0 &&
                  pixel_list.map((each, index) => {
                    return (
                      <option key={index} value={each.id}>
                        {each.name}({each.id})
                      </option>
                    );
                  })}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                select
                SelectProps={{
                  native: true,
                }}
                label="Media Buyer"
                id="media_buyer"
                name="media_buyer"
                onChange={handleChange}
                value={users && form_data.media_buyer}
                error={form_error.media_buyer ? true : false}
                helperText={form_error.media_buyer}
              >
                <option value="" disabled />
                {users &&
                  users.map((each, index) => {
                    return (
                      <option key={index} value={each.name}>
                        {each.name}
                      </option>
                    );
                  })}
              </TextField>
            </Grid>
          </Grid>
          <Grid item xs={12} spacing={2} container alignItems="flex-start" justifyContent="flex-start">
            <Grid item xs={6}>
              <TextField
                required
                fullWidth
                size="small"
                margin="dense"
                variant="outlined"
                label="Facebook Pixel Access Token"
                id="pixel_access_token"
                name="pixel_access_token"
                onChange={handleChange}
                value={form_data.pixel_access_token}
                error={form_error.pixel_access_token ? true : false}
                helperText={form_error.pixel_access_token}
              />
            </Grid>
          </Grid>
        </Fragment>
      ) : (
        <Skeleton variant="rect" height={100} width="100%" className={classes.skeleton} />
      )}
    </Grid>
  );
};

export default Section_02;
